import { Filter, Query } from "@cubejs-client/core"
import { useMemo, useState } from "react"
import {
  MetricsMapping,
  type PostProcessResultSync,
} from "~/ui-rtk/constants/metrics-mapping"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import useDate from "~/ui-rtk/hooks/date"
import { DATE_FORMAT } from "~/ui-rtk/hooks/period"
import {
  SessionStorageKeys,
  useSessionStorage,
} from "~/ui-rtk/hooks/session-storage"
import useUrlSorting from "~/ui-rtk/hooks/url-sorting"
import {
  getPivotData,
  polyfillCubeQueryWithTimeDimension,
} from "~/ui-rtk/utils/cube"
import { CHART_ID, DEFAULT_SORTING } from "../constants/home"

const cache: Record<string, unknown> = {}
const query: Record<string, Query> = {}

const dayjs = useDate()

const today = dayjs().startOf("day").toDate()

export default function useTop10BrandAds(dateStart: Date) {
  const { q, postProcess } = MetricsMapping.brand_media_optimization_ads_table
  const { id } = useCurrentCompany() ?? { id: null }
  const [hiddenCardIds, setHiddenCardIds] = useSessionStorage<string[]>(
    SessionStorageKeys.HIDE_TOP_10_BRAND_ADS,
    [],
  )
  const [, rerender] = useState(0)

  const { sorting } = useUrlSorting(CHART_ID, [DEFAULT_SORTING])
  const dateStartStart = dayjs(dateStart).startOf("day")
  const formattedDateStart = `${id}-${dateStartStart.format(DATE_FORMAT)}-${JSON.stringify(sorting)}`

  if (!query[formattedDateStart]) {
    const qWithSorting: Partial<Query> = {
      ...(q as Partial<Query>),
    }
    if (sorting[0]) {
      qWithSorting.order = [
        [
          `company_media_metrics_ads.${sorting[0].id}`,
          sorting[0].desc ? "desc" : "asc",
        ],
      ]
    }
    query[formattedDateStart] = polyfillCubeQueryWithTimeDimension(
      {
        ...qWithSorting,
        filters: [
          {
            dimension: "company_media_metrics_ads.brand_value_over_spend",
            operator: "gt",
            values: ["0.75"],
          },
          {
            dimension: "company_media_metrics_ads.spend",
            operator: "gt",
            values: ["250"],
          },
        ] as Filter[],
        limit: 10,
      } as Query,
      {
        dateRange: [dateStartStart.toDate(), today],
      },
    )
  }

  const cubeQuery = useCubeQuery(query[formattedDateStart])
  const { resultSet, isLoading } = cubeQuery

  if (resultSet) {
    // Sync postProcess; cast away the Promise side of the union type.
    const pivotData = postProcess?.(
      getPivotData(resultSet, query[formattedDateStart]),
      {},
    ) as PostProcessResultSync | undefined

    if (pivotData?.[0]) {
      cache[formattedDateStart] = pivotData[0]
    }
  }

  const data = useMemo(
    () =>
      (hiddenCardIds ?? []).length > 0
        ? (
            (cache[formattedDateStart] ?? []) as Record<string, string>[]
          ).filter(
            (itm: Record<string, string>) =>
              !(hiddenCardIds ?? []).includes(itm.ad_id),
          )
        : (cache[formattedDateStart] as Record<string, string>[]),
    [hiddenCardIds, cache],
  )

  const hide = (metric: Record<string, string>) => {
    setHiddenCardIds(
      Array.from(new Set([...(hiddenCardIds ?? []), metric.ad_id])),
    )
    rerender(i => i + 1)
  }

  return {
    data,
    isLoading,
    isStarted:
      JSON.stringify(cubeQuery.previousQuery) ===
      JSON.stringify(query[formattedDateStart]),
    hide,
  }
}
